import bem from "easy-bem";
import ActivityFeed from "features/activity-log/blocks/ActivityFeed";
import CommentsFeed from "features/comments/blocks/CommentsFeed";
import { useCheckNotifications } from "features/notifications/useCheckNotifications";
import { useDataQuery } from "hooks/useData";
import { useFlag } from "hooks/useFlags";
import { useSearchParam } from "hooks/useSearchParam";
import { useUser } from "hooks/useUser";
import React, { useEffect, useState } from "react";
import LoadingPlaceholder from "ui/LoadingPlaceholder";

import "./UserContent.css";
import { Card, Problem, Solution } from "../types/basic";

import ContentItemsView from "./ContentItemsView";
import SortToggle from "./SortToggle";
import type { SortField, SortDirection } from "./SortToggle.types";
import ViewSwitch from "./ViewSwitch";

const cn = bem("user-content");

type UserContentProps = {
  openLinksInNewWindow?: boolean;
  isModeratorial?: boolean;
  isOwnProfile?: boolean;
  personId?: number;
};

const UserContent: React.FC<UserContentProps> = ({
  openLinksInNewWindow = false,
  isModeratorial = false,
  isOwnProfile = false,
  personId,
}) => {
  const { userId, isModerator } = useUser();

  const commentsEnabled = useFlag("commentsEnabled");

  const defaultKind = isModeratorial ? "moderations" : "mine";

  // Открытие с колокольчика (?subtab=notifications, см. NotificationButton) сразу
  // открывает вкладку "Уведомления" — читается только один раз, при монтировании,
  // дальнейшее переключение вкладок в URL не пишется.
  const [subtabParam] = useSearchParam("subtab");
  const initialKind =
    isOwnProfile && subtabParam === "notifications"
      ? "notifications"
      : defaultKind;

  const [kind, setKind] = useState<
    | "mine"
    | "drafts"
    | "favorites"
    | "moderations"
    | "deletions"
    | "comments"
    | "activity"
    | "notifications"
  >(initialKind);

  const notificationsEnabled = useFlag("notificationsTest");
  const { check } = useCheckNotifications();

  // Открытие своей ленты комментариев = «я это посмотрел».
  // Только своя лента: чужой профиль и модераторская лента ничего не отмечают.
  useEffect(() => {
    if (
      kind === "comments" &&
      isOwnProfile &&
      commentsEnabled &&
      notificationsEnabled
    ) {
      check("comments");
    }
  }, [kind, isOwnProfile, commentsEnabled, notificationsEnabled, check]);

  // Открытие вкладки "Уведомления" = «я их посмотрел» — тот же приём, что и у комментариев.
  useEffect(() => {
    if (kind === "notifications" && isOwnProfile && notificationsEnabled) {
      check("activity");
    }
  }, [kind, isOwnProfile, notificationsEnabled, check]);

  let dataUrl = "";
  switch (kind) {
    case "mine":
      dataUrl = `content/person/${personId}/cases/`;
      break;
    case "drafts":
      dataUrl = `users/${userId}/drafts/`;
      break;
    case "favorites":
      dataUrl = `users/${userId}/favorites/`;
      break;
    case "moderations":
      dataUrl = isModeratorial
        ? isModerator
          ? `content/moderatorial/`
          : null
        : `content/moderated/`;
      break;
    case "deletions":
      dataUrl = `content/deleted/`;
      break;

    case "comments":
      dataUrl = null;
      break;

    case "activity":
      dataUrl = null;
      break;

    case "notifications":
      dataUrl = null;
      break;

    default:
      dataUrl = null;
      break;
  }

  const content = useDataQuery(dataUrl, dataUrl === null, undefined);

  const [sortField, setSortField] = useState<SortField>("dateCreated");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const [groupByCase, setGroupByCase] = useState<boolean>(false);

  const [showList, setShowList] = useState<boolean>(false);

  const [_filteredOutItems, setFilteredOutItems] = useState<
    Array<{ kind: string; id: number }>
  >([]);

  const moderatorialEnabled = useFlag("moderationEnabled");
  const contentLists = useFlag("contentLists");

  const variants = useDataQuery(`attribute_variants/`);

  const handleSortChange = (
    field: SortField,
    direction: SortDirection,
    groupByCase: boolean,
  ) => {
    setSortField(field);
    setSortDirection(direction);
    setGroupByCase(groupByCase);
  };

  const controls = (
    <div className={cn("controls")}>
      <ViewSwitch
        value={kind}
        onChange={(value) => {
          if (value !== "reviews") setKind(value);
        }}
        isModeratorial={isModerator && moderatorialEnabled && isModeratorial}
        isMyContent={isOwnProfile}
      />
      <SortToggle
        onSortChange={handleSortChange}
        initialField={sortField}
        initialDirection={sortDirection}
        initialGroupByCase={groupByCase}
        initialShowList={false}
        onDisplayModeChange={setShowList}
      />
    </div>
  );

  if (kind === "comments" && commentsEnabled) {
    return (
      <div className={cn()}>
        {controls}
        <CommentsFeed personId={isOwnProfile ? undefined : personId} />
      </div>
    );
  }

  if (kind === "activity") {
    return (
      <div className={cn()}>
        {controls}
        <ActivityFeed personId={isOwnProfile ? undefined : personId} />
      </div>
    );
  }

  if (kind === "notifications") {
    return (
      <div className={cn()}>
        {controls}
        <ActivityFeed feed="notifications" />
      </div>
    );
  }

  if (content === null) {
    return (
      <p>
        Ошибка при загрузке данных. Возможно, у вас недостаточно прав для
        просмотра этого контента.
      </p>
    );
  }

  if (!content) {
    return <LoadingPlaceholder />;
  }

  const { cards = [], problems = [], solutions = [] } = content;

  const allItems: Array<Card | Problem | Solution> = [
    ...cards,
    ...problems,
    ...solutions,
  ];

  const itemCount = allItems.length;

  if (
    !contentLists &&
    !(moderatorialEnabled && ["moderations", "deletions"].includes(kind))
  ) {
    return null;
  }

  const sortItems = (items: Array<Card | Problem | Solution>) => {
    return [...items].sort((a, b) => {
      const aDate = new Date(a.meta?.[sortField]).getTime();
      const bDate = new Date(b.meta?.[sortField]).getTime();

      if (sortDirection === "asc") {
        return aDate - bDate;
      } else {
        return bDate - aDate;
      }
    });
  };

  const sortedItems = sortItems(allItems);

  const findVariantTitle = (variantId: number | null) => {
    const variant = variants?.find((v: any) => v?.id === variantId);

    return variant ? variant.title : "";
  };

  return (
    <div className={cn()}>
      {controls}

      <div>Всего найдено: {itemCount}</div>

      <ContentItemsView
        items={sortedItems}
        solutions={solutions}
        showList={showList}
        sortField={sortField}
        openLinksInNewWindow={openLinksInNewWindow}
        findVariantTitle={findVariantTitle}
        parentQueryId={dataUrl}
        onItemSaved={(item) => {
          setFilteredOutItems((prev) => [
            ...prev,
            { kind: item.contentType, id: item.id },
          ]);
        }}
      />
    </div>
  );
};

export default UserContent;
